import {
  CellClassParams,
  CellStyle,
  ColDef,
  EditableCallbackParams,
  ITooltipParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { NfTooltipParams } from '@northfork/ag-grid'
import {
  getInlineEditColumnType,
  InlineEditDataType,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions } from '@northfork/ng-basics'
import {
  customSortComparatorBulkNumeric,
  numberValueFormatter,
} from './bulk-specific-common-utils'

export const LOAN_FIELD_COLUMNS = [
  {
    field: 'interestBearingInterestReserve',
    headerName: 'Interest Reserve (Interest Bearing)',
  },
  {
    field: 'interestBearingPrincipalReserve',
    headerName: 'Principal Reserve (Interest Bearing)',
  },
  {
    field: 'nonInterestBearingInterestReserve',
    headerName: 'Interest Reserve (Non-Interest Bearing)',
  },
  {
    field: 'nonInterestBearingPrincipalReserve',
    headerName: 'Principal Reserve (Non-Interest Bearing)',
  },
] as const

export type LoanFieldKey = typeof LOAN_FIELD_COLUMNS[number]['field']

export interface LoanFieldColumnCallbacks {
  fieldPath: (field: LoanFieldKey) => string
  getCellStyle: (params: CellClassParams) => CellStyle
  toolTipValueGetter: (params: ITooltipParams) => string
  showToolTip: (params?: ITooltipParams) => boolean
}

export function getLoanFieldValues(
  getRawValue: (field: LoanFieldKey) => unknown,
): Partial<Record<LoanFieldKey, number>> {
  const values: Partial<Record<LoanFieldKey, number>> = {}
  for (const { field } of LOAN_FIELD_COLUMNS) {
    const raw = getRawValue(field)
    if (raw) {
      values[field] = Number(raw)
    }
  }
  return values
}

type ExternalLoanFields = Partial<Record<LoanFieldKey, number | string>> & {
  balanceToAdjust?: string
  loanFeeType?: string
}

/** Loan fields of an external transaction; each may sit on the transaction or in its aggInfo DataMap. */
export function getExternalLoanFields(
  transaction: ExternalLoanFields,
  aggInfo?: { DataMap?: ExternalLoanFields },
) {
  const dataMap = aggInfo?.DataMap
  return {
    balanceToAdjust: transaction.balanceToAdjust ?? dataMap?.balanceToAdjust,
    loanFeeType: transaction.loanFeeType ?? dataMap?.loanFeeType,
    ...getLoanFieldValues(field => transaction[field] ?? dataMap?.[field]),
  }
}

// Used by bulk load: an inline-editable number column shown in the "open" group state.
export function createBulkLoadLoanFieldColumns(
  callbacks: LoanFieldColumnCallbacks,
): ColDef[] {
  return LOAN_FIELD_COLUMNS.map(({ field, headerName }) => ({
    field: callbacks.fieldPath(field),
    headerName,
    width: 100,
    type: getInlineEditColumnType(),
    cellEditorParams: {
      dataType: InlineEditDataType.Number,
      disablePopover: true,
    },
    cellRendererParams: {
      editButtonClickOnly: true,
    } as InlineEditDisplayOptions,
    cellStyle: (params: CellClassParams) => callbacks.getCellStyle(params),
    tooltipValueGetter: (params: ITooltipParams) =>
      callbacks.toolTipValueGetter(params),
    tooltipComponentParams: {
      showTooltip: (params?: ITooltipParams) => callbacks.showToolTip(params),
    } as NfTooltipParams,
    columnGroupShow: 'open',
    editable: true,
  }))
}

// Used by bulk load: per-basis reserve columns inside a basis' "Details" column group.
export function createBulkLoadBasisLoanFieldColumns(
  basisPrefix: string,
  applicableAccounts: number[],
  callbacks: Pick<
    LoanFieldColumnCallbacks,
    'fieldPath' | 'getCellStyle' | 'showToolTip'
  >,
): ColDef[] {
  return LOAN_FIELD_COLUMNS.map(({ field, headerName }) => ({
    field: callbacks.fieldPath(field),
    headerName: `${basisPrefix} ${headerName}`,
    type: getInlineEditColumnType(),
    cellRendererParams: {
      editButtonClickOnly: true,
    } as InlineEditDisplayOptions,
    cellEditorParams: {
      dataType: InlineEditDataType.Number,
    },
    cellStyle: (params: CellClassParams) => callbacks.getCellStyle(params),
    tooltipComponentParams: {
      showTooltip: (params?: ITooltipParams) => callbacks.showToolTip(params),
    } as NfTooltipParams,
    editable: (params: EditableCallbackParams) =>
      applicableAccounts.includes(params.data.accountId),
    columnGroupShow: 'open',
    comparator: customSortComparatorBulkNumeric,
    valueFormatter: (params: ValueFormatterParams) => params.value ?? '',
  }))
}

// Used by bulk trade entry: a sortable, numeric-formatted column, headed by "Loan <name>".
export function createBulkTradeEntryLoanFieldColumns(
  callbacks: LoanFieldColumnCallbacks,
): ColDef[] {
  return LOAN_FIELD_COLUMNS.map(({ field, headerName }) => ({
    field: callbacks.fieldPath(field),
    headerName: `Loan ${headerName}`,
    width: 90,
    type: getInlineEditColumnType(),
    cellEditorParams: {
      dataType: InlineEditDataType.Number,
      disablePopover: true,
    },
    comparator: customSortComparatorBulkNumeric,
    cellRendererParams: {
      editButtonClickOnly: true,
    } as InlineEditDisplayOptions,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value !== ''
        ? numberValueFormatter(Number(params.value), 2, 2)
        : '',
    cellStyle: (params: CellClassParams) => callbacks.getCellStyle(params),
    tooltipValueGetter: (params: ITooltipParams) =>
      callbacks.toolTipValueGetter(params),
    tooltipComponentParams: {
      showTooltip: (params?: ITooltipParams) => callbacks.showToolTip(params),
    } as NfTooltipParams,
  }))
}
